import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Dropdown, Table, Input, Select, Spin, Empty, message, Progress } from "antd";
import {
  MoreOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { PageHeader, EngagementsWrapper, EngagementTableWrapper } from "./Engagements.style";
import axios from "axios";
import { API_CONST } from "../../../const";
import { PermissionGate } from "../../../components/PermissionGate/PermissionGate";
import { usePermissions } from "../../../contexts/PermissionContext";

const { Option } = Select;

const statusColors = {
  pending_approval: "orange",
  active: "green",
  closure_requested: "gold",
  closed: "default",
  rejected: "red",
};

const statusLabels = {
  pending_approval: "Pending Approval",
  active: "Active",
  closure_requested: "Closure Requested",
  closed: "Closed",
  rejected: "Rejected",
};

const urgencyColors = {
  low: "default",
  medium: "blue",
  high: "orange",
  critical: "red",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `₹ ${parseFloat(value).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const CustomerEngagements = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [viewMode, setViewMode] = useState("grid");
  const [activeTab, setActiveTab] = useState("all");
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    pendingApproval: 0,
    active: 0,
    closed: 0,
    rejected: 0,
  });

  const fetchEngagements = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const status = activeTab === "all" ? undefined : activeTab;
      
      const response = await axios.post(
        API_CONST.GET_ENGAGEMENTS,
        { status, search: searchText, limit: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEngagements(response.data.engagements || []);
    } catch (error) {
      console.error("Error fetching engagements:", error);
      message.error("Failed to load engagements");
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchText]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ENGAGEMENT_STATS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data.stats || {});
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchEngagements();
    fetchStats();
  }, [fetchEngagements, fetchStats]);

  const handleViewDetails = (id) => {
    navigate(`/customer/engagement/${id}`);
  };

  const handleEditEngagement = (id) => {
    navigate(`/customer/edit-engagement/${id}`);
  };

  const handleRequestClosure = async (engagement) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.REQUEST_ENGAGEMENT_CLOSURE}/${engagement.id}/request-closure`,
        { reason: "Requested by customer" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Closure request submitted");
      fetchEngagements();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to request closure");
    }
  };

  const getActionMenuItems = (engagement) => {
    const items = [];
    
    if (hasPermission('engagement.view')) {
      items.push({
        key: "view",
        label: "View Details",
        icon: <EyeOutlined />,
        onClick: () => handleViewDetails(engagement.id),
      });
    }

    if ((engagement.status === "pending_approval" || engagement.status === "rejected") && hasPermission('engagement.edit')) {
      items.push({
        key: "edit",
        label: engagement.status === "rejected" ? "Edit & Resubmit" : "Edit",
        icon: <EditOutlined />,
        onClick: () => handleEditEngagement(engagement.id),
      });
    }

    if (engagement.status === "active" && hasPermission('engagement.edit')) {
      items.push({
        key: "closure",
        label: "Request Closure",
        icon: <CloseCircleOutlined />,
        danger: true,
        onClick: () => handleRequestClosure(engagement),
      });
    }

    return items;
  };

  const getHealthBadge = (health, type) => {
    if (!health) return null;
    const status = health[type];
    return (
      <span className={`health-badge ${status}`}>
        {status === "good" && <CheckCircleOutlined />}
        {status === "warning" && <ClockCircleOutlined />}
        {status === "critical" && <CloseCircleOutlined />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const tableColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      render: (id) => <span style={{ fontSize: 12, color: "#8c8c8c" }}>{id}</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <span className="engagement-title-link" onClick={() => handleViewDetails(record.id)}>
          {title}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "engagementType",
      key: "engagementType",
      width: 120,
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>,
    },
    {
      title: "Duration Health",
      key: "durationHealth",
      width: 130,
      render: (_, record) => record.status === "active" ? getHealthBadge(record.health, "duration") : "-",
    },
    {
      title: "Budget Health",
      key: "budgetHealth",
      width: 130,
      render: (_, record) => record.status === "active" ? getHealthBadge(record.health, "budget") : "-",
    },
    {
      title: "Budget",
      key: "budget",
      width: 220,
      render: (_, record) => {
        const totalBudget = parseFloat(record.totalBudget) || 0;
        const budgetSpent = parseFloat(record.budgetSpent) || 0;
        const percent = totalBudget > 0 ? Math.min((budgetSpent / totalBudget) * 100, 100) : 0;
        const strokeColor = percent >= 90 ? '#ff4d4f' : percent >= 70 ? '#faad14' : '#52c41a';
        
        return (
          <div style={{ minWidth: 180 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: '#8c8c8c' }}>Spent: {formatCurrency(budgetSpent)}</span>
              <span style={{ color: '#8c8c8c' }}>/ {formatCurrency(totalBudget)}</span>
            </div>
            <Progress
              percent={Math.round(percent)}
              size="small"
              strokeColor={strokeColor}
              format={(pct) => `${pct}%`}
            />
          </div>
        );
      },
    },
    {
      title: "Start Date",
      dataIndex: "expectedStartDate",
      key: "expectedStartDate",
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Rejection Reason",
      dataIndex: "rejectionReason",
      key: "rejectionReason",
      width: 200,
      render: (reason, record) => 
        record.status === "rejected" && reason ? (
          <span style={{ color: "#cf1322" }}>{reason}</span>
        ) : "-",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={["click"]}>
          <MoreOutlined style={{ cursor: "pointer", fontSize: 18 }} />
        </Dropdown>
      ),
    },
  ];

  return (
    <EngagementsWrapper>
      <PageHeader>
        <h1>My Engagements</h1>
        <p>View and manage your active engagements</p>
      </PageHeader>
      <Row gutter={16} className="metrics-row">
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable onClick={() => setActiveTab("active")}>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f6ffed" }}>
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.active || 0}</h3>
                <p className="metric-label">Active Engagements</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable onClick={() => setActiveTab("pending_approval")}>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#fff7e6" }}>
                <ClockCircleOutlined style={{ color: "#fa8c16", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.pendingApproval || 0}</h3>
                <p className="metric-label">Pending Approval</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#fff1f0" }}>
                <DollarOutlined style={{ color: "#cf1322", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{formatCurrency(stats.totalSpent)}</h3>
                <p className="metric-label">Total Spent</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f0f5ff" }}>
                <DollarOutlined style={{ color: "#597ef7", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{formatCurrency(stats.totalBudget)}</h3>
                <p className="metric-label">Total Budget</p>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <div className="tabs-section">
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            className={`tab-button ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All ({stats.total || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "pending_approval" ? "active" : ""}`}
            onClick={() => setActiveTab("pending_approval")}
          >
            Pending ({stats.pendingApproval || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "active" ? "active" : ""}`}
            onClick={() => setActiveTab("active")}
          >
            Active ({stats.active || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "closed" ? "active" : ""}`}
            onClick={() => setActiveTab("closed")}
          >
            Closed ({stats.closed || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "rejected" ? "active" : ""}`}
            onClick={() => setActiveTab("rejected")}
          >
            Rejected ({stats.rejected || 0})
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="view-toggle">
            <Button
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode("grid")}
            />
            <Button
              className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
              icon={<UnorderedListOutlined />}
              onClick={() => setViewMode("table")}
            />
          </div>
          <PermissionGate permission="engagement.create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/customer/create-engagement")}
            >
              Create Engagement
            </Button>
          </PermissionGate>
        </div>
      </div>

      <div className="filters-section">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search engagements..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Spin spinning={loading}>
        {viewMode === "grid" ? (
          <div className="engagements-grid">
            {engagements.length === 0 && !loading ? (
              <Card className="empty-card">
                <Empty description="No engagements found" />
              </Card>
            ) : (
              engagements.map((eng) => (
                <Card key={eng.id} className="engagement-card" hoverable>
                  <div className="card-header">
                    <div>
                      <p className="engagement-id">{eng.id}</p>
                      <h3 className="engagement-title" onClick={() => handleViewDetails(eng.id)}>
                        {eng.title}
                      </h3>
                    </div>
                    <Dropdown menu={{ items: getActionMenuItems(eng) }} trigger={["click"]}>
                      <MoreOutlined className="action-icon" />
                    </Dropdown>
                  </div>

                  <div className="card-meta">
                    <Tag color="blue">{eng.engagementType}</Tag>
                    <Tag color={statusColors[eng.status]}>{statusLabels[eng.status]}</Tag>
                    {eng.hiringUrgency && (
                      <Tag color={urgencyColors[eng.hiringUrgency]}>
                        {eng.hiringUrgency.charAt(0).toUpperCase() + eng.hiringUrgency.slice(1)} Priority
                      </Tag>
                    )}
                  </div>

                  {eng.description && <p className="card-description">{eng.description}</p>}

                  {eng.status === "rejected" && eng.rejectionReason && (
                    <div className="rejection-reason" style={{ 
                      background: "#fff2f0", 
                      border: "1px solid #ffccc7", 
                      borderRadius: 6, 
                      padding: "8px 12px", 
                      marginTop: 12 
                    }}>
                      <strong style={{ color: "#cf1322" }}>Rejection Reason:</strong>
                      <p style={{ margin: "4px 0 0", color: "#595959" }}>{eng.rejectionReason}</p>
                    </div>
                  )}

                  {eng.status === "active" && eng.health && (
                    <div className="health-indicators">
                      <div className="health-item">
                        <span className="health-label">Duration:</span>
                        <span className="health-duration-value">{eng.expectedDuration || 0} {eng.durationUnit || "Months"}</span>
                        <span className={`health-value ${eng.health.duration}`}>
                          ({eng.health.duration === "good" && "On Track"}
                          {eng.health.duration === "warning" && "Ending Soon"}
                          {eng.health.duration === "critical" && "Overdue"})
                        </span>
                      </div>
                      <span className="health-separator">|</span>
                      <div className="health-item">
                        <span className="health-label">Budget:</span>
                        <span className={`health-value ${eng.health.budget}`}>
                          {eng.health.budget === "good" && "OK"}
                          {eng.health.budget === "warning" && "80%+"}
                          {eng.health.budget === "critical" && "Over"}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="budget-progress" style={{ marginTop: 12, padding: '8px 0' }}>
                    {(() => {
                      const totalBudget = parseFloat(eng.totalBudget) || 0;
                      const budgetSpent = parseFloat(eng.budgetSpent) || 0;
                      const percent = totalBudget > 0 ? Math.min((budgetSpent / totalBudget) * 100, 100) : 0;
                      const strokeColor = percent >= 90 ? '#ff4d4f' : percent >= 70 ? '#faad14' : '#52c41a';
                      return (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                            <span style={{ color: '#595959' }}>Spent: {formatCurrency(budgetSpent)}</span>
                            <span style={{ color: '#8c8c8c' }}>/ {formatCurrency(totalBudget)}</span>
                          </div>
                          <Progress
                            percent={Math.round(percent)}
                            size="small"
                            strokeColor={strokeColor}
                            format={(pct) => `${pct}%`}
                          />
                        </>
                      );
                    })()}
                  </div>

                </Card>
              ))
            )}
          </div>
        ) : (
          <EngagementTableWrapper>
            <Table
              columns={tableColumns}
              dataSource={engagements}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              scroll={{ x: 1200 }}
              locale={{ emptyText: <Empty description="No engagements found" /> }}
            />
          </EngagementTableWrapper>
        )}
      </Spin>
    </EngagementsWrapper>
  );
};

export default CustomerEngagements;
